if (!customElements.get("carousel-component")) {
  class Carousel extends HTMLElement {
    constructor() {
      super();

      try {
        this.carouselOptions = JSON.parse(this.dataset.carouselOptions || "{}");
      } catch (e) {
        console.error("Failed to parse carouselOptions:", e);
        this.carouselOptions = {};
      }

      this.isInitialized = false;
      this.carousel = null;
    }

    connectedCallback() {
      if (!this.classList.contains("splide")) return;
      
      // Check if this carousel is in the first few products (critical for LCP)
      const isAboveFold = this.isInAboveFoldProduct();
      
      if (isAboveFold) {
        // Initialize immediately for above-the-fold carousels
        this.initCarousel();
      } else {
        // Lazy initialize for below-the-fold carousels
        this.setupIntersectionObserver();
      }
    }

    isInAboveFoldProduct() {
      // Check if carousel is in the first 2 products (typically above fold)
      const productCard = this.closest('.grid__item');
      if (!productCard) return false;
      
      const allProducts = document.querySelectorAll('#product-grid .grid__item');
      const currentIndex = Array.from(allProducts).indexOf(productCard);
      
      // First 2 products on desktop, first 1 on mobile
      const threshold = window.innerWidth >= 750 ? 2 : 1;
      return currentIndex < threshold;
    }

    setupIntersectionObserver() {
      // Only initialize carousel when it's near viewport (lazy loading)
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isInitialized) {
              this.initCarousel();
              observer.unobserve(this);
            }
          });
        },
        {
          rootMargin: "100px", // Start loading 100px before entering viewport
          threshold: 0.01,
        }
      );

      observer.observe(this);
    }

    initCarousel() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      // Check if Splide is loaded
      if (typeof Splide === 'undefined') {
        console.error('Splide library not loaded');
        return;
      }

      this.carousel = new Splide(this, {
        ...this.carouselOptions,
        type: "slide",
        perPage: 1,
        arrows: true,
        pagination: false,
        drag: true,
        focus: "center",
        lazyLoad: "nearby", // Enable Splide's built-in lazy loading
        preloadPages: 1, // Only preload the next slide
        speed: 400, // Slightly faster transitions
        waitForTransition: true,
      });

      this.carousel.on("mounted", () => {
        if (this.dataset.progressBar === "true") {
          this.initProgressBar(this.carousel);
        }
        
        // Mark carousel as ready
        this.classList.add('carousel-ready');
      });

      // Error handling
      this.carousel.on("error", (error) => {
        console.error("Carousel error:", error);
      });

      try {
        this.carousel.mount();
      } catch (error) {
        console.error("Failed to mount carousel:", error);
      }
    }

    initProgressBar(carouselInstance) {
      const bar = this.querySelector(".splide-progress-bar");
      if (!bar) return;

      const track = bar.querySelector(".splide-progress-bar__track");
      if (!track) return;

      const updateProgress = () => {
        const end = carouselInstance.Components.Controller.getEnd() + 1;
        const currentIndex = carouselInstance.index;
        const rate = Math.min((currentIndex + 1) / end, 1);
        track.style.width = `${100 * rate}%`;
      };

      // Update progress on every move
      carouselInstance.on("move", updateProgress);

      // Immediately update progress after mounting
      requestAnimationFrame(() => {
        updateProgress();
      });
    }

    disconnectedCallback() {
      // Clean up when element is removed
      if (this.carousel) {
        try {
          this.carousel.destroy();
        } catch (error) {
          console.error("Failed to destroy carousel:", error);
        }
      }
    }
  }

  customElements.define("carousel-component", Carousel);
}